import React from "react";
import { Descriptions, Form, Input, Space, Typography } from "antd";
import { formatCurrency, hasValue } from "./quoteVersionUtils";
import { PendingChangeNote } from "./PendingChangeNote";
import { Skeleton } from "../../../components/Skeleton";

interface QuoteHeaderProps {
  soNo: string;
  versionLabel: string;
  orderDate: string;
  shipDate: string | null;
  poNumber: string;
  terms: string | null;
  depositRequired: number | null;
  // While true, depositRequired is still catching up with the debounced live recalc preview —
  // same Skeleton convention already used in PricingSummaryTable/HardwareReplacementModal.
  depositRequiredLoading?: boolean;
  editing: boolean;
  saving: boolean;
  draftPoNumber: string;
  onDraftPoNumberChange: (v: string) => void;
}

export const QuoteHeader: React.FC<QuoteHeaderProps> = ({
  soNo,
  versionLabel,
  orderDate,
  shipDate,
  poNumber,
  terms,
  depositRequired,
  depositRequiredLoading,
  editing,
  saving,
  draftPoNumber,
  onDraftPoNumberChange,
}) => {
  const quoteTitle = `${soNo}${versionLabel ? `-${versionLabel}` : ""}`;

  return (
    <Descriptions size="small" column={2} style={{ marginBottom: 12 }}>
      <Descriptions.Item label="Quote #">
        <Typography.Text strong>{quoteTitle}</Typography.Text>
      </Descriptions.Item>
      {hasValue(orderDate) && (
        <Descriptions.Item label="Order Date">{orderDate}</Descriptions.Item>
      )}
      {hasValue(shipDate) && (
        <Descriptions.Item label="Ship Date">{shipDate}</Descriptions.Item>
      )}
      {hasValue(poNumber) && !editing && (
        <Descriptions.Item label={"PO / Job"}>
          {poNumber}
        </Descriptions.Item>
      )}
      {hasValue(poNumber) && editing && (
        <Form.Item
          label="PO / Job"
          required
          validateStatus={!draftPoNumber.trim() ? "error" : ""}
          help={!draftPoNumber.trim() ? "Required" : undefined}
          style={{ margin: 0 }}
        >
          <Space direction="vertical">
            <Input
              value={draftPoNumber}
              onChange={(e) => onDraftPoNumberChange(e.target.value)}
              disabled={saving}
            />
            {draftPoNumber.trim() !== poNumber.trim() && <PendingChangeNote />}
          </Space>
        </Form.Item>
      )}
      {hasValue(terms) && (
        <Descriptions.Item label="Terms">{terms}</Descriptions.Item>
      )}
      {hasValue(depositRequired) && (
        <Descriptions.Item label="Deposit Required">
          {depositRequiredLoading ? (
            <Skeleton width="60px" height="16px" />
          ) : (
            formatCurrency(depositRequired)
          )}
        </Descriptions.Item>
      )}
    </Descriptions>
  );
};
